import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react"; 
import axios from "axios";

import io from "socket.io-client";
import { WorkspaceViewNode } from "../../components/ForumMode/StickerWorkspace/WorkspaceViewNode";
import { QuestionnaireNodeCreator } from "../../components/ForumMode/core/QuestionnaireNodeCreator";
import { NodeTypeSelector } from "../../components/ForumMode/core/NodeTypeSelector";
import Notice from "../../components/Notice";
import url from "../../url.js";
import { genEdge, genNode_without_emoji } from "../../utils/ideaTool";
import { ModeContext } from "../../contexts/ModeContext";
import './StickerWorkspace.css';
import { StickerNodeCreator as CreateIdeaModal } from "../../components/ForumMode/ForumSticker/StickerNodeCreator";
import { buildWorkspaceContext } from "../../components/ForumMode/StickerWorkspace/workspaceContext";
import { SelectableNode } from "../../components/ForumMode/StickerWorkspace/SelectableNode";
import { ToolPanel } from "../../components/ForumMode/StickerWorkspace/ToolPanel";
import GroupToolPanel from "../../components/GroupToolPanel";
import { getNodeType } from "../../utils/nodeTypeUtils";
import { resolveViewNodeComponent } from "../../components/ForumMode/nodeViewRegistry";
import useUnreadMessages from "../../hooks/useUnreadMessages";
import colors from '../../styles/colors';
import { Button, Typography } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SelectAllIcon from '@mui/icons-material/SelectAll';

import {
  ReactFlow,
  MiniMap,
  Controls,
  ControlButton,
  Background,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';


// Convert fetched nodes and edges data to initialNodes and initialEdges
const convertToInitialData = (safeNodes, safeEdges) => {
  // 建立 node map 與邊圖
  const nodeMap = new Map();
  const inDegree = new Map();
  const outDegree = new Map();
  const graph = new Map();
  const reverseGraph = new Map();

  safeNodes.forEach((node) => {
    if (node && node.id) {
      nodeMap.set(node.id.toString(), node);
      inDegree.set(node.id.toString(), 0);
      outDegree.set(node.id.toString(), 0);
      graph.set(node.id.toString(), []);
      reverseGraph.set(node.id.toString(), []);
    }
  });

  safeEdges.forEach((edge) => {
    if (edge && edge.from && edge.to) {
      const from = edge.from.toString();
      const to = edge.to.toString();
      
      if (graph.has(from)) {
        graph.get(from).push(to);
      }
      if (reverseGraph.has(to)) {
        reverseGraph.get(to).push(from);
      }
      
      if (inDegree.has(to)) {
        inDegree.set(to, inDegree.get(to) + 1);
      }
      
      if (outDegree.has(from)) {
        outDegree.set(from, outDegree.get(from) + 1);
      }
    }
  });

  // 找出所有葉節點
  const leafNodes = [];
  for (const [id, outDeg] of outDegree.entries()) {
    if (outDeg === 0) {
      leafNodes.push(id);
    }
  }
  
  // 計算層級
  const levels = new Map();
  const visited = new Set();
  
  const calculateLevels = (nodeId, level) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    levels.set(nodeId, level);
    
    const parents = reverseGraph.get(nodeId) || [];
    for (const parent of parents) {
      calculateLevels(parent, level + 1);
    }
  };
  
  leafNodes.forEach(leafId => {
    calculateLevels(leafId, 0);
  });
  
  for (const [id] of nodeMap.entries()) {
    if (!levels.has(id)) {
      levels.set(id, 0);
    }
  }
  
  // 按層級分組
  const levelGroups = new Map();
  for (const [id, level] of levels.entries()) {
    if (!levelGroups.has(level)) {
      levelGroups.set(level, []);
    }
    levelGroups.get(level).push(id);
  }
  
  // 計算位置
  const initialNodes = [];
  const nodePositions = new Map();
  const verticalSpacing = 300;
  const horizontalSpacing = 250;
  
  const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => b - a);
  const totalHeight = sortedLevels.length * verticalSpacing;
  const startY = totalHeight / 2;
  
  sortedLevels.forEach(level => {
    const nodesInLevel = levelGroups.get(level);
    const y = startY - (level * verticalSpacing);
    const totalWidth = nodesInLevel.length * horizontalSpacing;
    const startX = -totalWidth / 2;
    
    nodesInLevel.forEach((nodeId, index) => {
      const x = startX + index * horizontalSpacing;
      nodePositions.set(nodeId, { x, y });
      
      const node = nodeMap.get(nodeId);
      if (node) {
        initialNodes.push({
          id: nodeId,
          position: { x, y },
          data: { 
            label: node.label ? node.label.split("\n")[0] : node.title, 
            source: node,
            isLeafNode: level === 0
          },
          type: 'selectableNode',
        });
      }
    });
  });

  const initialEdges = safeEdges.map((edge) => {
    if (edge && edge.from && edge.to) {
      return {
        id: `e${edge.from}-${edge.to}`,
        source: edge.from.toString(),
        target: edge.to.toString(),
        info: edge,
      };
    }
    return null;
  }).filter(edge => edge !== null);

  return { initialNodes, initialEdges };
};

const buildLayoutSnapshot = (nodesSnapshot, edgesSnapshot) => {
  const safeNodes = nodesSnapshot.map(node => {
    const source = node.data?.source || {};
    return {
      ...source,
      id: source.id ?? node.id,
      label: source.label ?? node.data?.label ?? source.title ?? '',
      title: source.title ?? source.label ?? node.data?.label ?? ''
    };
  });

  const safeEdges = edgesSnapshot.map(edge => {
    const raw = edge.info || {};
    return {
      from: raw.from ?? raw.source ?? edge.source,
      to: raw.to ?? raw.target ?? edge.target,
    };
  });

  return convertToInitialData(safeNodes, safeEdges);
};

/**
 * StickerWorkspace - 便利貼工作區模式
 * 
 * 這是一個基於 ForumSticker 的擴展模式：
 * - 左右佈局：左邊是可勾選節點的便利貼區（70%），右邊是工具欄（30%）
 * - 工具欄分上下兩區：工具列表和結果展示區
 * - 結果區支援分頁，每個分頁是一個新的結果展示（不存入資料庫）
 */
export default function StickerWorkspace({ showNavbar = true }) {
  // 初始化：將 localStorage 同步到 sessionStorage
  useEffect(() => {
    const syncStorage = () => {
      const activityId = localStorage.getItem('activityId');
      const groupId = localStorage.getItem('groupId');
      const userId = localStorage.getItem('userId');
      const joinCode = localStorage.getItem('joinCode');
      const userName = localStorage.getItem('userName');
      
      // 從 activityInfo 提取 activityTitle
      try {
        const activityInfoStr = localStorage.getItem('activityInfo');
        if (activityInfoStr) {
          const info = JSON.parse(activityInfoStr);
          const title = info?.Activity?.title || info?.title || info?.activityTitle || '';
          if (title) {
            sessionStorage.setItem('activityTitle', title);
          }
        }
      } catch (e) {
        console.warn('[StickerWorkspace] 解析 activityInfo 失敗:', e);
      }
      
      if (activityId) sessionStorage.setItem('activityId', activityId);
      if (groupId) sessionStorage.setItem('groupId', groupId);
      if (userId) sessionStorage.setItem('userId', userId);
      if (joinCode) sessionStorage.setItem('joinCode', joinCode);
      if (userName) sessionStorage.setItem('userName', userName);
      
      console.log('[StickerWorkspace] Storage 同步完成:', { activityId, groupId, userId, joinCode });
    };
    
    syncStorage();
  }, []);
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';  const activityInfo = JSON.parse(localStorage.getItem('activityInfo') || 'null');
  const { isMapBoard } = useContext(ModeContext);
  const { updateLastReadTime, clearCache } = useUnreadMessages();
  const [open, setOpen] = useState(false);
  const [nodeContent, setNodeContent] = useState(null);
  const [ws, setWS] = useState(null);
  const [notices, setNotices] = useState([]);
  const activityId = sessionStorage.getItem("activityId");
  const networkRef = useRef(null);
  
  // 節點和邊狀態
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // 選中節點狀態
  const [selectedNodes, setSelectedNodes] = useState(new Set());
  
  // 其他狀態
  const [draggedNodeId, setDraggedNodeId] = useState(null);
  const [groupId, setGroupId] = useState(sessionStorage.getItem('groupId'));
  const [nodeTypeSelectorOpen, setNodeTypeSelectorOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [scaffoldNodePlan, setScaffoldNodePlan] = useState(null);
  const [questionnaireCreateOpen, setQuestionnaireCreateOpen] = useState(false);
  const [toolPanelWidth, setToolPanelWidth] = useState(400);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [showMiniMap, setShowMiniMap] = useState(true);
  
  // 標記已讀
  useEffect(() => {
    const gid = sessionStorage.getItem('groupId');
    const aid = sessionStorage.getItem('activityId');
    if (!gid || !aid || !updateLastReadTime) return;

    const groupIdNum = parseInt(gid, 10);
    const activityIdNum = parseInt(aid, 10);

    updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
      console.error('StickerWorkspace 標記已讀失敗:', err)
    );

    const interval = setInterval(() => {
      updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
        console.error('StickerWorkspace 定期更新已讀失敗:', err)
      );
    }, 120000);

    return () => clearInterval(interval);
  }, [groupId, updateLastReadTime]);

  // 監聽螢幕尺寸變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 頁面可見性變化
  useEffect(() => {
    const gid = sessionStorage.getItem('groupId');
    const aid = sessionStorage.getItem('activityId');
    if (!gid || !aid || !updateLastReadTime) return;

    const groupIdNum = parseInt(gid, 10);
    const activityIdNum = parseInt(aid, 10);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateLastReadTime(groupIdNum, activityIdNum).catch(err => 
          console.error('StickerWorkspace 可見性變化更新已讀失敗:', err)
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [groupId, updateLastReadTime]);

  // Refs
  const edgesRef = useRef(edges);
  const nodesRef = useRef(nodes);
  
  useEffect(() => {
    edgesRef.current = edges;
    nodesRef.current = nodes;
  }, [edges, nodes]);

  // 切換節點選擇 - 更新節點的 data 來觸發重新渲染
  const handleToggleSelect = useCallback((nodeId) => {
    setSelectedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // 當 selectedNodes 變化時，更新節點的 data 以觸發重新渲染
  useEffect(() => {
    setNodes(prevNodes => 
      prevNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          _selectedNodes: selectedNodes, // 將選擇狀態附加到節點 data
        }
      }))
    );
  }, [selectedNodes, setNodes]);

  // 全選節點
  const handleSelectAll = useCallback(() => {
    const allNodeIds = new Set(nodes.map(n => n.id?.toString()));
    setSelectedNodes(allNodeIds);
  }, [nodes]);

  // 清除選擇
  const handleClearSelection = useCallback(() => {
    setSelectedNodes(new Set());
  }, []);

  const applyAutoLayout = useCallback(() => {
    const { initialNodes } = buildLayoutSnapshot(nodesRef.current, edgesRef.current);
    const layoutMap = new Map(initialNodes.map(node => [node.id, node.position]));
    setNodes(prev => prev.map(node => {
      const position = layoutMap.get(node.id);
      return position ? { ...node, position } : node;
    }));
  }, [setNodes]);
  
  const handleClickOpenByNodeId = useCallback((node) => {
    setNodeContent(null);
    setOpen(true);

    // 初始節點（包含問卷初始節點）帶有 isInitial 標記，直接用本地數據，不呼叫後端 API
    const isInitialNode = node?.isInitial === true;
    if (isInitialNode) {
      setNodeContent(node);
    } else {
      fetchNode(node.id);
    }

    if (networkRef.current) {
      networkRef.current.focus(node.id, { scale: 1.5, offset: { x: 0, y: 0 } });
    }
  }, []);

  // 自訂 Node 元件 - 使用節點 data 中的選擇狀態
  const nodeTypes = useMemo(() => {
    return {
      selectableNode: (props) => (
        <SelectableNode 
          {...props}
          handleClickOpenByNodeId={handleClickOpenByNodeId}
          selectedNodes={props.data._selectedNodes || new Set()}
          onToggleSelect={handleToggleSelect}
        />
      ),
    };
  }, [handleClickOpenByNodeId, handleToggleSelect]);

  const handleClose = () => {
    setOpen(false);
  };

  const fetchNode = async (nodeId) => {
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    const activityId = sessionStorage.getItem('activityId');
    const groupId = sessionStorage.getItem('groupId');
    
    try {
      let response;
      if (isGuestMode) {
        // 访客模式：使用公开 API
        response = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/node/${nodeId}`);
      } else {
        // 认证模式：使用认证 API
        response = await axios.get(`${url.backendHost}api/nodes/${nodeId}`);
      }
      setNodeContent({ 
        ...response.data, 
        renderedContent: { __html: response.data.content }
      });
    } catch (err) {
      console.error(err);
    }
  };

  // 獲取 groupId
  const fetchGroupData = async () => {
    const jwtToken = localStorage.getItem('jwtToken');
    const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    
    const joinCode = sessionStorage.getItem('joinCode');
    const activityId = sessionStorage.getItem('activityId');
    const userId = sessionStorage.getItem('userId');
    
    // 參數驗證（訪客模式跳過 userId 檢查）
    if (!isGuestMode && (!activityId || activityId === 'null' || !userId || userId === 'null')) {
      console.error('[StickerWorkspace] 缺少必要參數:', { activityId, userId });
      alert('無法獲取活動資訊，請重新進入活動');
      window.location.href = '/main';
      return null;
    }
    
    // 訪客模式不需要獲取群組資料，直接返回
    if (isGuestMode) {
      console.log('[StickerWorkspace] 訪客模式，跳過獲取群組資料');
      return await getNodes();
    }
    
    const enterDifferentGroupEndpoint = `${url.backendHost}api/groups/teacher/${joinCode}/${userId}`;
    const getMyGroupEndpoint = `${url.backendHost}api/groups/${activityId}/${userId}`;
    
    console.log('[StickerWorkspace] 獲取群組資料:', { activityId, userId, joinCode });

    try {
      if (!joinCode) throw new Error('no-join-code');
      const response = await axios.get(enterDifferentGroupEndpoint, { headers, withCredentials: true });
      const fetchedGroupId = response.data.data[0].id;
      localStorage.setItem("groupId", fetchedGroupId);
      sessionStorage.setItem("groupId", fetchedGroupId);
      setGroupId(fetchedGroupId);
    } catch (error) {
      try {
        const response = await axios.get(getMyGroupEndpoint, { headers, withCredentials: true });
        const fetchedGroupId = response.data.data[0].id;
        localStorage.setItem("groupId", fetchedGroupId);
        sessionStorage.setItem("groupId", fetchedGroupId);
        setGroupId(fetchedGroupId);
      } catch (err) {
        console.error("獲取群組資料時出錯", err);
      }
    } finally {
      return await getNodes();
    }
  };

  // Initial fetch
  useEffect(() => {
    let isActive = true;
    (async () => {
      setWS(io.connect(url.socketioHost, { path: '/s/socket.io' }));

      await fetchGroupData().then(async (data) => {
        let allNodes = [...data.nodes];
        let allEdges = [...data.edges];
        
        // Add initial nodes
        try {
          const { loadInitialNodesForSticker, validateInitialNodes } = require('../../utils/initialNodesLoader');
          const groupId = sessionStorage.getItem('groupId');
          const initialNodes = loadInitialNodesForSticker(activityInfo, groupId);
          
          if (initialNodes.length > 0) {
            if (!validateInitialNodes(initialNodes)) {
              console.error('[StickerWorkspace] Invalid initial nodes detected');
            }
            allNodes = [...allNodes, ...initialNodes];
          }
        } catch (e) {
          console.error('[StickerWorkspace] Error loading initial nodes:', e);
        }
        
        const { initialNodes, initialEdges } = convertToInitialData(allNodes, allEdges);

        const nodesWithPositions = initialNodes.map(node => {
          if (node.position) {
            return node;
          }
          return {
            ...node,
            position: { x: 100, y: 100 }
          };
        });

        if (!isActive) return;
        setNodes(nodesWithPositions);
        setEdges(initialEdges);
      });
    })();
    return () => { isActive = false; };
  }, []);
  
  // WebSocket events
  useEffect(() => {
    if (ws) {
      ws.on("connect", () => {
        addNotice("Connected to server successfully", "success");
      });

      ws.on(`node-recieve-${activityId}`, (body) => {
        if (body.groupId == sessionStorage.getItem('groupId')) {
          const newNode = {
            createdAt: body.createdAt || new Date().toISOString(),
            author: body.author,
            ...genNode_without_emoji(body),
          };
          
          const reactFlowNode = {
            id: newNode.id.toString(),
            position: { x: 100, y: 100 },
            data: { 
              label: newNode.label ? newNode.label.split("\n")[0] : newNode.title, 
              source: newNode 
            },
            type: 'selectableNode',
          };
          
          setNodes((prevNodes) => [...prevNodes, reactFlowNode]);
          
          if (clearCache) {
            clearCache(body.groupId);
          }
        }
      });

      ws.on(`edge-recieve-${activityId}`, (body) => {
        if (body.groupId == sessionStorage.getItem("groupId")) {
          const newEdge = genEdge(body);
          
          const reactFlowEdge = {
            id: `e${newEdge.from}-${newEdge.to}`,
            source: newEdge.from.toString(),
            target: newEdge.to.toString(),
            info: newEdge,
          };
          
          setEdges((prevEdges) => [...prevEdges, reactFlowEdge]);
        }
      });

      return () => {
        ws.off(`node-recieve-${activityId}`);
        ws.off("connect");
      };
    }
  }, [ws, nodes, edges, activityId, groupId]);

  const getNodes = async () => {
    const groupId = sessionStorage.getItem("groupId");
    const isGuestMode = sessionStorage.getItem('isGuestMode') === 'true';
    const activityId = sessionStorage.getItem('activityId');
    
    if (!groupId || groupId === 'null') {
      console.log('[StickerWorkspace] groupId 無效，嘗試獲取群組資料');
      if (!isGuestMode) {
        await fetchGroupData();
      }
      return;
    }

    try {
      const jwtToken = localStorage.getItem('jwtToken');
      
      // 訪客模式使用公開 API，否則使用認證 API
      let fetchData, fetchEdge;
      if (isGuestMode) {
        // 訪客模式：不發送 JWT token，使用公開 API
        fetchData = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/nodes`);
        fetchEdge = await axios.get(`${url.backendHost}api/guest/${activityId}/${groupId}/edges`);
      } else {
        // 認證模式：發送 JWT token
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : undefined;
        fetchData = await axios.get(`${url.backendHost}api/nodes/all/${groupId}`, { headers, withCredentials: true });
        fetchEdge = await axios.get(`${url.backendHost}api/edges/all/${groupId}`, { headers, withCredentials: true });
      }
      
      // 處理節點數據：訪客模式和認證模式的數據結構不同
      const nodeData = (isGuestMode ? fetchData.data : fetchData.data[0].Nodes).map((node) => {
        const createdAt = node.createdAt ? new Date(node.createdAt) : null;
        const formattedCreatedAt = createdAt ? createdAt.toISOString() : 'Invalid Date';

        return {
          ...genNode_without_emoji(node),
          createdAt: formattedCreatedAt,
          author: node.author,
          authorId: node.authorId,
        };
      });

      const edgeData = fetchEdge.data.map((edge) => genEdge(edge));

      if (!Array.isArray(nodeData)) { 
        addNotice("Invalid node data format", "error");
        throw new Error("Invalid node data format");
      }
      if (!Array.isArray(edgeData)) {
        addNotice("Invalid edge data format", "error");
        throw new Error("Invalid edge data format");
      }

      addNotice("Data loaded successfully", "success");
      return {
        nodes: nodeData,
        edges: edgeData,
      };
    } catch (error) {
      console.error('Error fetching data:', error);
      addNotice("Failed to load data. Please try again.", "error");
      if (error.response?.status === 401) {
        addNotice("Session expired. Please login again.", "warning");
        window.location.href = "/";
      }
      throw error;
    }
  };

  // Refs
  const reactFlowContainerRef = useRef(null);
  const reactFlowInstanceRef = useRef(null);
  const refreshDebounceRef = useRef(null);
  
  // Handle resize
  useEffect(() => {
    if (!reactFlowContainerRef.current) return;
    
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setNodes(prevNodes => [...prevNodes]);
      }, 100);
    };
    
    try {
      const resizeObserver = new ResizeObserver(entries => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || entries.length === 0) return;
          handleResize();
        });
      });
      
      resizeObserver.observe(reactFlowContainerRef.current);
      
      return () => {
        clearTimeout(resizeTimeout);
        resizeObserver.disconnect();
      };
    } catch (error) {
      console.error("Error setting up ResizeObserver:", error);
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(resizeTimeout);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  // Drag handlers
  const onNodeDragStart = useCallback((event, node) => {
    setDraggedNodeId(node.id);
  }, []);

  const onNodeDrag = useCallback((event, node) => {
    // 拖拽中
  }, []);

  const onNodeDragEnd = useCallback(() => {
    setDraggedNodeId(null);
  }, []);

  // Notice functions
  const addNotice = (message, type = 'info') => {
    const id = Date.now();
    setNotices(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotices(prev => prev.filter(notice => notice.id !== id));
    }, 5000);
  };

  const removeNotice = (id) => {
    setNotices(prev => prev.filter(notice => notice.id !== id));
  };

  // Refresh function
  const refreshMindMap = useCallback((options = { preserveFocus: true, animate: true }) => {
    if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    refreshDebounceRef.current = setTimeout(() => {
      applyAutoLayout();

      setTimeout(() => {
        const inst = reactFlowInstanceRef.current;
        if (!inst) return;
        try {
          if (typeof inst.fitView === 'function') {
            inst.fitView({ padding: 0.1 });
          }
        } catch (e) {
          console.warn('fitView not available', e);
        }

        const preserveId = sessionStorage.getItem('nodeId');
        if (options.preserveFocus && preserveId) {
          const node = nodesRef.current.find(n => n.id == preserveId);
          if (node) {
            try {
              if (typeof inst.setCenter === 'function') {
                inst.setCenter(node.position.x, node.position.y, { duration: options.animate ? 300 : 0 });
              }
            } catch (e) {
              console.warn('Failed to center on node', e);
            }
          }
        }
      }, 50);
    }, 150);
  }, [setNodes]);

  useEffect(() => {
    try {
      window.refreshMindMap = refreshMindMap;
    } catch (e) {}
    return () => {
      try {
        if (window.refreshMindMap === refreshMindMap) delete window.refreshMindMap;
      } catch (e) {}
    };
  }, [refreshMindMap]);

  return (
    <div className="workspace-container">
      {/* Notice Container */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        {notices.map(notice => (
          <Notice
            key={notice.id}
            type={notice.type}
            message={notice.message}
            onClose={() => removeNotice(notice.id)}
          />
        ))}
      </div>

      {/* 左側便利貼區域 */}
      <div 
        className="workspace-sticker-area" 
        style={!isMobile ? { width: `calc(100% - ${toolPanelWidth}px)` } : undefined}
      >
        {/* 選擇計數器 */}
        <div className="selection-counter">
          <Typography variant="body2">
            已選擇 <span className="count">{selectedNodes.size}</span> 個節點
          </Typography>
          <div className="actions">
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
              disabled={nodes.length === 0}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              全選
            </Button>
            <Button
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleClearSelection}
              disabled={selectedNodes.size === 0}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              清除
            </Button>
          </div>
        </div>

        {/* Workspace 模式始終顯示 ReactFlow，不依賴 isMapBoard */}
        {(isMapBoard !== false) && (
          <div 
            ref={reactFlowContainerRef}
            style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden'
            }}
          >
            <ReactFlow
              onInit={(inst) => { reactFlowInstanceRef.current = inst; }}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeDragStart={onNodeDragStart}
              onNodeDrag={onNodeDrag}
              onNodeDragStop={onNodeDragEnd}
              proOptions={{ hideAttribution: true }}
              nodeTypes={nodeTypes}
              nodesDraggable={!isGuestMode}
              nodesConnectable={!isGuestMode}
              fitView
              panOnScroll={true}
              panOnDrag={[1, 2]}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={false}
              preventScrolling={true}
              selectNodesOnDrag={false}
              defaultEdgeOptions={{
                type: 'smoothstep',
                animated: false,
                style: { strokeWidth: 1, stroke: '#555' },
                markerEnd: {
                  type: 'arrow',
                  color: '#000',
                  width: 60,
                  height: 60,
                },
              }}
            >
              <Controls>
                <ControlButton onClick={() => setNodeTypeSelectorOpen(true)} title="新增節點" style={{ order: -1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 15a4 4 0 0 1-4 4H8l-4 3V7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="9" cy="11" r="1" fill="currentColor"/>
                    <circle cx="12" cy="11" r="1" fill="currentColor"/>
                    <circle cx="15" cy="11" r="1" fill="currentColor"/>
                  </svg>
                </ControlButton>
                <ControlButton onClick={() => {
                  if (typeof window.refreshMindMap === 'function') {
                    window.refreshMindMap({ preserveFocus: true, animate: true });
                  }
                }} title="重整心智圖" style={{ order: -1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="5" r="2" fill="currentColor"/>
                    <circle cx="7" cy="12" r="2" fill="currentColor"/>
                    <circle cx="17" cy="12" r="2" fill="currentColor"/>
                    <circle cx="7" cy="19" r="2" fill="currentColor"/>
                    <circle cx="17" cy="19" r="2" fill="currentColor"/>
                    <line x1="12" y1="7" x2="7" y2="10" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="12" y1="7" x2="17" y2="10" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="7" y1="14" x2="7" y2="17" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="17" y1="14" x2="17" y2="17" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </ControlButton>
                <ControlButton onClick={() => setShowMiniMap(!showMiniMap)} title={showMiniMap ? "隱藏小地圖" : "顯示小地圖"} style={{ order: -1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showMiniMap ? (
                      <g>
                        {/* 地圖折疊線條 */}
                        <path d="M2 6 L8 4 L14 6 L22 4 V18 L14 20 L8 18 L2 20 V6 Z" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="1.5" 
                          strokeLinejoin="round"/>
                        <line x1="8" y1="4" x2="8" y2="18" stroke="currentColor" strokeWidth="1"/>
                        <line x1="14" y1="6" x2="14" y2="20" stroke="currentColor" strokeWidth="1"/>
                        {/* X 關閉符號 */}
                        <line x1="5" y1="5" x2="19" y2="19" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
                        <line x1="19" y1="5" x2="5" y2="19" stroke="#ff4444" strokeWidth="2.5" strokeLinecap="round"/>
                      </g>
                    ) : (
                      <g>
                        {/* 地圖折疊線條 */}
                        <path d="M2 6 L8 4 L14 6 L22 4 V18 L14 20 L8 18 L2 20 V6 Z" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="1.5" 
                          strokeLinejoin="round"/>
                        <line x1="8" y1="4" x2="8" y2="18" stroke="currentColor" strokeWidth="1"/>
                        <line x1="14" y1="6" x2="14" y2="20" stroke="currentColor" strokeWidth="1"/>
                      </g>
                    )}
                  </svg>
                </ControlButton>
              </Controls>
              {showMiniMap && <MiniMap />}
              <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
          </div>
        )}
        
      </div>

      {/* 右側工具欄區域 */}
      <div 
        className="workspace-tool-area" 
        style={!isMobile ? { width: `${toolPanelWidth}px`, flex: 'none' } : undefined}
      >
        <ToolPanel
          selectedNodes={selectedNodes}
          graphNodes={nodes}
          getNodeContent={(nodeId) => {
            const node = nodes.find(n => n.id === nodeId);
            return node?.data?.source || null;
          }}
          onWidthChange={setToolPanelWidth}
          onScaffoldCreateNode={(detail) => {
            setScaffoldNodePlan(detail);
            setCreateOpen(true);
          }}
          initialWidth={toolPanelWidth}
        />
      </div>

      {/* 節點詳情對話框 */}
      {(() => {
        const ViewNode = resolveViewNodeComponent(getNodeType(nodeContent), 'workspace');
        if (!ViewNode) return null;
        return (
          <ViewNode
            open={open}
            onClose={handleClose}
            nodeContent={nodeContent}
            ws={ws}
            groupId={groupId}
            activityId={activityId}
            graphNodes={nodes}
            graphEdges={edges}
            buildGroupContext={buildWorkspaceContext}
            mode="workspace"
            onRefresh={getNodes}
            onNodeUpdated={(nodeId) => {
              if (nodeContent && nodeContent.id === nodeId) {
                fetchNode(nodeId);
              }
            }}
            onNodeDeleted={(nodeId) => {
              if (nodeContent && nodeContent.id === nodeId) {
                setNodeContent(null);
                setOpen(false);
              }
            }}
          />
        );
      })()}

      {/* 節點類型選擇器 */}
      <NodeTypeSelector
        open={nodeTypeSelectorOpen}
        onClose={() => setNodeTypeSelectorOpen(false)}
        onSelect={(type) => {
          if (type === 'questionnaire') {
            setQuestionnaireCreateOpen(true);
          } else {
            setCreateOpen(true);
          }
        }}
      />

      {/* 創建節點對話框（含鷹架帶入的預填與特殊標籤） */}
      {createOpen && <CreateIdeaModal 
        open={createOpen} 
        onClose={() => {
          setCreateOpen(false);
          setScaffoldNodePlan(null);
        }} 
        ws={ws} 
        graphNodes={nodes}
        graphEdges={edges}
        activityId={activityId}
        groupId={groupId}
        nodePlan={scaffoldNodePlan ? {
          title: scaffoldNodePlan.title || "新增想法",
          label: "想法標題",
          tags: scaffoldNodePlan.scaffoldTag,
          helperMsg: "可編輯標題與內容後送出，此節點將帶有對應鷹架標籤。",
          initialTitle: scaffoldNodePlan.title,
          initialContent: scaffoldNodePlan.content,
        } : {
          title: "新增想法",
          label: "想法標題",
          tags: "idea",
          helperMsg: "請為你的想法下一個標題，讓其他同學能更快速的你的想法！",
          saffoldBody: [
            "【💡我的想法】",
            "【🧐我覺得更好的想法】",
            "【❓我想知道】",
            "【🙅🏻這個想法不能解釋】",
            "【📄舉例和參考來源】",
            "【✍🏻我的總結】"
          ]
        }}
        buildGroupContext={buildWorkspaceContext}
        mode="workspace"
      />}

      {/* 問卷節點建立對話框 */}
      {questionnaireCreateOpen && (
        <QuestionnaireNodeCreator
          open={questionnaireCreateOpen}
          onClose={() => setQuestionnaireCreateOpen(false)}
          ws={ws}
          groupId={groupId}
          activityId={activityId}
        />
      )}

      {/* 小組工具面板（浮動） */}
      {!isGuestMode && (
        <GroupToolPanel
          onNodeAdded={(newNode) => {
            // 當從 GroupToolPanel 添加新節點時，同步到視圖
            if (newNode) {
              getNodes();
            }
          }}
          activityId={activityId}
          groupId={groupId}
          mode="workspace"
          ws={ws}
        />
      )}
    </div>
  );
}
